'use client';

/**
 * CMS Iframe Component
 *
 * Renders external content via iframe with:
 * - Brand-aware domain validation (security)
 * - Locale/CRM ID injection via URL placeholders
 * - Optional responsive height via @iframe-resizer/react
 * - Loading state handling
 * - Route query param passthrough
 *
 * Multi-brand support:
 * - Breitling: *.breitling.com, breitling.secure.force.com
 * - Gallet: *.gallet.com, *.galletwatches.com
 *
 * URL Placeholders:
 * - %%COUNTRY%% → Country slug (e.g., 'us')
 * - %%LANGUAGE%% → Language slug (e.g., 'en')
 * - %%SFID%% → SalesForce ID from user metadata
 *
 * @example
 * ```tsx
 * <Iframe
 *   url="https://forms.breitling.com/warranty?country=%%COUNTRY%%&lang=%%LANGUAGE%%&sfid=%%SFID%%"
 *   height={600}
 *   maxWidth="lg"
 *   internalName="Warranty Registration Form"
 * />
 * ```
 */

import clsx from 'clsx';
import { useParams, useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';

import { ACCOUNT_METADATA, getMetaValue, getSalesForceId, type AuthUser } from '@/lib/auth';
import { getBrandConfig, isUrlAllowedForIframe } from '@/lib/brands';
import { devWarn } from '@/lib/utils/devLog';
import {
  injectUrlPlaceholders,
  mergeRouteParamsIntoUrl,
  parseLocaleForUrl,
  requiresUserAuth,
} from '@/lib/utils/urlInjection';

import styles from './Iframe.module.scss';

/**
 * Maximum width options for iframe container
 */
export type IframeMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full';

/**
 * Iframe component props
 */
export interface IframeProps {
  /** URL to embed (can contain placeholders) */
  url?: string | null;
  /** Fixed height in pixels (if not set, uses iframe-resizer for auto height) */
  height?: number | string | null;
  /** Enable scrollbar in iframe */
  hasScrollbar?: boolean | null;
  /** Maximum width of the iframe container */
  maxWidth?: IframeMaxWidth | null;
  /** Internal name for accessibility (used as title) */
  internalName?: string | null;
  /** CMS index for data-testid */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
  /** User data for SFID injection (passed from server) */
  user?: AuthUser | null;
}

/**
 * Loading spinner component
 */
function Loader({ testId }: { testId?: string }) {
  return (
    <div className={styles.loader} data-testid={testId}>
      <div className={styles.loaderSpinner} aria-hidden="true" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

/**
 * CMS Iframe Component
 */
export function Iframe({
  url,
  height,
  hasScrollbar = false,
  maxWidth = 'full',
  internalName,
  cmsIndex,
  className,
  user,
}: IframeProps) {
  const params = useParams();
  const searchParams = useSearchParams();

  // Loading state
  const [loading, setLoading] = useState(true);

  // Generate test IDs
  const testId = (element?: string) =>
    cmsIndex !== undefined
      ? element
        ? `iframe-${cmsIndex}-${element}`
        : `iframe-${cmsIndex}`
      : undefined;

  // Get current brand config
  const brandConfig = getBrandConfig();

  // Parse locale from route params
  const locale = (params?.lang as string) || 'us-en';
  const { countrySlug, languageSlug } = parseLocaleForUrl(locale);

  // Check if we need to wait for user data - user comes from server so no loading state needed
  const needsUserAuth = url ? requiresUserAuth(url) : false;
  const isLoadingUser = false; // User data is passed from server, no client-side loading

  // Build final iframe URL
  const finalUrl = useMemo(() => {
    if (!url) return null;

    // Validate domain
    if (!isUrlAllowedForIframe(url, brandConfig)) {
      devWarn(
        `[Iframe] Blocked URL from non-allowed domain: ${url}`,
        `Allowed: ${brandConfig.allowedIframeDomains.join(', ')}`
      );
      return null;
    }

    // Get user's CRM ID (SalesForce ID)
    const crmId = user
      ? getMetaValue(user.metadata, ACCOUNT_METADATA.salesForceId) || getSalesForceId(user)
      : undefined;

    // Inject placeholders
    let processedUrl = injectUrlPlaceholders(url, {
      countrySlug,
      languageSlug,
      crmId,
    });

    // Merge route query params (UTM params, etc.)
    if (searchParams) {
      processedUrl = mergeRouteParamsIntoUrl(processedUrl, searchParams, ['slug', 'lang']);
    }

    return processedUrl;
  }, [url, brandConfig, countrySlug, languageSlug, user, searchParams]);

  // Handle load complete
  const handleLoad = () => {
    setLoading(false);
  };

  // Container class based on maxWidth
  const containerClass = clsx(
    styles.container,
    {
      [styles.containerSm]: maxWidth === 'sm',
      [styles.containerMd]: maxWidth === 'md',
      [styles.containerLg]: maxWidth === 'lg',
      [styles.containerXl]: maxWidth === 'xl',
      [styles.containerXxl]: maxWidth === 'xxl',
      [styles.containerFull]: maxWidth === 'full',
    },
    className
  );

  // No URL or blocked domain
  if (!url || !finalUrl) {
    return null;
  }

  // Waiting for user data when SFID is needed
  if (needsUserAuth && isLoadingUser) {
    return (
      <div className={containerClass}>
        <Loader testId={testId('loader')} />
      </div>
    );
  }

  // Parse height - if it's a number or numeric string, use fixed height iframe
  const numericHeight = typeof height === 'number' ? height : Number(height);
  const useFixedHeight = !isNaN(numericHeight) && numericHeight > 0;

  // Iframe content
  const iframeContent = (
    <div className={styles.wrapper} data-testid={testId()}>
      {/* Loading state */}
      {loading && <Loader testId={testId('loader')} />}

      {/* Iframe */}
      {useFixedHeight ? (
        // Fixed height iframe
        <iframe
          src={finalUrl}
          height={numericHeight}
          title={internalName || 'Embedded content'}
          scrolling={hasScrollbar ? 'auto' : 'no'}
          onLoad={handleLoad}
          className={clsx(styles.iframe, { [styles.hidden]: loading })}
          data-testid={testId('frame')}
        />
      ) : (
        // Auto-height iframe using iframe-resizer
        // Note: iframe-resizer needs to be installed and the embedded page needs
        // the iframe-resizer child script. Falls back to fixed min-height if not.
        <IframeResizerWrapper
          src={finalUrl}
          title={internalName || 'Embedded content'}
          onLoad={handleLoad}
          loading={loading}
          testId={testId('resizer')}
        />
      )}
    </div>
  );

  // Wrap in container if not full width
  if (maxWidth === 'full') {
    return iframeContent;
  }

  return <div className={containerClass}>{iframeContent}</div>;
}

/**
 * Wrapper for iframe-resizer
 * Dynamically imports the library to avoid SSR issues
 */
interface IframeResizerWrapperProps {
  src: string;
  title: string;
  onLoad: () => void;
  loading: boolean;
  testId?: string;
}

function IframeResizerWrapper({ src, title, onLoad, loading, testId }: IframeResizerWrapperProps) {
  // Note: @iframe-resizer/react needs to be installed:
  // npm install @iframe-resizer/react
  //
  // The embedded page also needs the child script:
  // <script src="https://cdn.jsdelivr.net/npm/@iframe-resizer/child"></script>
  //
  // For pages that don't have the child script, the iframe will render
  // with a default min-height.

  // License key from environment
  const _license = process.env.NEXT_PUBLIC_IFRAME_RESIZER_LICENSE || '';

  // Dynamic import to avoid SSR issues
  // For now, we'll use a standard iframe with a fallback height
  // The full iframe-resizer integration requires:
  // 1. npm install @iframe-resizer/react
  // 2. NEXT_PUBLIC_IFRAME_RESIZER_LICENSE env var
  // 3. Child script on embedded pages

  // TODO: Uncomment when @iframe-resizer/react is installed
  // import IframeResizer from '@iframe-resizer/react';
  // return (
  //   <IframeResizer
  //     src={src}
  //     title={title}
  //     license={license}
  //     onLoad={onLoad}
  //     scrolling="omit"
  //     style={{ width: '100%', minHeight: '400px' }}
  //     className={clsx(styles.iframeResizer, { [styles.hidden]: loading })}
  //     data-testid={testId}
  //   />
  // );

  // Fallback: Standard iframe with default height
  // This will be used until iframe-resizer is installed
  return (
    <iframe
      src={src}
      title={title}
      onLoad={onLoad}
      scrolling="auto"
      style={{ width: '100%', minHeight: '600px', height: '100%' }}
      className={clsx(styles.iframeResizer, { [styles.hidden]: loading })}
      data-testid={testId}
    />
  );
}

Iframe.displayName = 'Iframe';
